import { useEffect } from 'react';
import { useNotifications, PRESENCE_SCOPE, createCoalescer, isAbortError } from '@medprompt/shared';

/**
 * Keeps the open document in step with the server: update-stream refreshes, deletion, the
 * filed-away hand-off to auto-navigation, reconnect catch-up, and presence.
 */
const useDocumentLiveUpdates = ({
  documentId,
  doc,
  user,
  documentService,
  insightsService,
  updateDoc,
  showDeletedPlaceholder,
  onFiledAwayByCurrentUser,
  refetchDocument,
  refetchPatient,
}) => {
  const { addDocumentUpdateCallback, addReconnectCallback, addNotification, setViewing } = useNotifications();
  const docId = doc?.documentId;
  const userId = user?.userId;

  useEffect(() => {
    if (!addDocumentUpdateCallback || !documentId) return;

    // A document being generated emits a burst of progress events. Each one used to trigger its
    // own detail fetch; coalescing collapses the burst into one, and the controller cancels a
    // fetch that a newer event has already superseded.
    let refreshAbort = null;
    const coalescer = createCoalescer(async () => {
      refreshAbort?.abort();
      const controller = new AbortController();
      refreshAbort = controller;

      try {
        const updatedDocumentData = await documentService.fetchDocumentData(
          documentId, { signal: controller.signal });

        // Ignore a response that landed after the user moved to another document.
        updateDoc(prevDoc => (
          prevDoc && prevDoc.documentId === documentId ? updatedDocumentData : prevDoc
        ));
      } catch (error) {
        if (isAbortError(error)) return;
        console.error(`DocumentDetailView: Error fetching updated data for ${documentId}:`, error);
        addNotification(`update-fetch-error-${documentId}`, 'Failed to fetch updated document data', { isError: true });
      } finally {
        if (refreshAbort === controller) {
          refreshAbort = null;
        }
      }
    });

    const handleDocumentUpdate = (updatedDocumentId, statusTransition) => {
      if (updatedDocumentId !== documentId) return;

      // Deleted documents stay on this route so prev/next and the audit trail remain available.
      if (statusTransition.to === 'NON_EXISTENT') {
        showDeletedPlaceholder();
        return;
      }

      // Filing completed by the current user: hand off to auto-navigation. Prefer Next; fall
      // back to Prev at end of list.
      if (
        statusTransition.from === 'QUEUED_FOR_FILING' &&
        statusTransition.to === 'FILED_AWAY' &&
        statusTransition.userId &&
        statusTransition.userId === userId
      ) {
        onFiledAwayByCurrentUser();
      }

      coalescer.push(updatedDocumentId);
    };

    const unsubscribe = addDocumentUpdateCallback(handleDocumentUpdate);

    return () => {
      unsubscribe();
      coalescer.cancel();
      refreshAbort?.abort();
    };
  }, [addDocumentUpdateCallback, documentId, documentService, updateDoc, addNotification, showDeletedPlaceholder, onFiledAwayByCurrentUser, userId]);

  // Anything that changed while the connection was down produced no event for this tab, so the
  // open document has to be refetched on reconnect the way the list views already refetch.
  useEffect(() => {
    if (!addReconnectCallback || !documentId) return;

    return addReconnectCallback(async () => {
      const refreshed = await refetchDocument();
      if (refreshed) {
        refetchPatient();
      }
    });
  }, [addReconnectCallback, documentId, refetchDocument, refetchPatient]);

  // Record document opened, then hold presence over the update socket. This used to be a POST
  // every 15 seconds per open document, which cost a request thread each time.
  useEffect(() => {
    if (!docId) return;

    insightsService.recordDocumentOpened(docId)
      .then(() => console.log('Document opened event recorded successfully for:', docId))
      .catch((error) => console.error('Failed to record document opened event:', error));
    setViewing({ scope: PRESENCE_SCOPE.documents, itemId: docId });

    return () => setViewing(null);
  }, [docId, insightsService, setViewing]);
};

export default useDocumentLiveUpdates;
